import React from 'react';

enum InvoiceStatus {
    PAID = 'Paid',
    PENDING = 'Pending',
    OVERDUE = 'Overdue',
}

interface InvoiceItem {
    readonly number: string;
    readonly client: string;
    readonly amount: string;
    readonly issueDate: string;
    readonly dueDate: string;
    readonly status: InvoiceStatus;
}

interface InvoiceFeatureItemProps {
    readonly icon: React.ReactNode;
    readonly iconClassName: string;
    readonly title: string;
    readonly description: string;
}

interface InvoiceStatCardProps {
    readonly label: string;
    readonly value: string;
    readonly icon: React.ReactNode;
    readonly iconClassName: string;
}

interface InvoiceIntegrationCardProps {
    readonly logo: string;
    readonly logoClassName: string;
    readonly name: string;
}

interface InvoiceReminderMetricProps {
    readonly icon: React.ReactNode;
    readonly value: string;
    readonly label: string;
}

interface PayrollFeatureItem {
    readonly title: string;
    readonly description: string;
    readonly bullets: readonly string[];
    readonly visual: React.ReactNode;
}

interface PayrollVisualIconProps {
    readonly icon: React.ReactNode;
}

interface TimeClockEmployee {
    readonly name: string;
    readonly role: string;
    readonly location: string;
    readonly time: string;
    readonly initials: string;
    readonly positionTop: string;
    readonly positionLeft: string;
}

interface TimeClockSummaryItemProps {
    readonly icon: React.ReactNode;
    readonly iconClassName: string;
    readonly title: string;
    readonly description: string;
}

interface TimeClockMetricItemProps {
    readonly label: string;
    readonly value: string;
    readonly change: string;
    readonly icon: React.ReactNode;
    readonly iconClassName: string;
    readonly changeClassName: string;
}

interface TimeClockAvatarProps {
    readonly initials: string;
    readonly size?: 'sm' | 'md';
}

enum SmartMatchWorkerAvatarTone {
    BLUE = 'is-blue',
    GREEN = 'is-green',
    PURPLE = 'is-purple',
    PINK = 'is-pink',
}

interface SmartMatchWorkerMatch {
    readonly initials: string;
    readonly name: string;
    readonly role: string;
    readonly rate: string;
    readonly rateLabel: string;
    readonly matchScore: string;
    readonly avatarTone: SmartMatchWorkerAvatarTone;
}

interface SmartMatchShiftDetail {
    readonly label: string;
    readonly value: string;
    readonly isPriorityBadge?: boolean;
}

interface SmartMatchFeatureListItem {
    readonly text: string;
}

interface SmartMatchStatItem {
    readonly value: string;
    readonly label: string;
}

enum WorkforceComplianceStatus {
    COMPLIANT = 'Compliant',
    EXPIRING_SOON = 'Expiring Soon',
}

enum WorkforceMetricIconTone {
    CYAN = 'is-cyan',
    BLUE = 'is-blue',
    VIOLET = 'is-violet',
    AMBER = 'is-amber',
    EMERALD = 'is-emerald',
    PINK = 'is-pink',
}

enum WorkforceMetricDetailTone {
    POSITIVE = 'is-positive',
    WARNING = 'is-warning',
    NEUTRAL = 'is-neutral',
}

enum WorkforceWorkerAvatarTone {
    BLUE = 'is-blue',
    ORANGE = 'is-orange',
    VIOLET = 'is-violet',
    EMERALD = 'is-emerald',
    PINK = 'is-pink',
}

enum WorkforceComplianceDotTone {
    EMERALD = 'is-emerald',
    AMBER = 'is-amber',
    RED = 'is-red',
    SLATE = 'is-slate',
}

interface WorkforceMetricItem {
    readonly label: string;
    readonly value: string;
    readonly detail: string;
    readonly icon: React.ReactNode;
    readonly iconTone: WorkforceMetricIconTone;
    readonly detailTone: WorkforceMetricDetailTone;
}

interface WorkforceWorkerItem {
    readonly initials: string;
    readonly name: string;
    readonly role: string;
    readonly certifications: number;
    readonly status: WorkforceComplianceStatus;
    readonly avatarTone: WorkforceWorkerAvatarTone;
}

interface WorkforceComplianceItem {
    readonly label: string;
    readonly value: string;
    readonly dotTone: WorkforceComplianceDotTone;
}

interface WorkforceCardHeaderProps {
    readonly icon: React.ReactNode;
    readonly title: string;
    readonly action?: string;
}

interface TrustVaultDocumentItem {
    readonly name: string;
    readonly type: string;
}

interface TrustVaultAlertItem {
    readonly name: string;
    readonly expiry: string;
}

interface TrustVaultWorkerItem {
    readonly initials: string;
    readonly name: string;
    readonly document: string;
}

export {
    InvoiceStatus,
    type InvoiceFeatureItemProps,
    type InvoiceIntegrationCardProps,
    type InvoiceItem,
    type InvoiceReminderMetricProps,
    type InvoiceStatCardProps,
    type PayrollFeatureItem,
    type PayrollVisualIconProps,
    type TimeClockAvatarProps,
    type TimeClockEmployee,
    type TimeClockMetricItemProps,
    type TimeClockSummaryItemProps,
    SmartMatchWorkerAvatarTone,
    type SmartMatchFeatureListItem,
    type SmartMatchShiftDetail,
    type SmartMatchStatItem,
    type SmartMatchWorkerMatch,
    WorkforceComplianceStatus,
    WorkforceMetricIconTone,
    WorkforceMetricDetailTone,
    WorkforceWorkerAvatarTone,
    WorkforceComplianceDotTone,
    type WorkforceMetricItem,
    type WorkforceWorkerItem,
    type WorkforceComplianceItem,
    type WorkforceCardHeaderProps,
    type TrustVaultDocumentItem,
    type TrustVaultAlertItem,
    type TrustVaultWorkerItem,
};
